import { useState, useEffect } from "react";
import { AdminBoardItemDto } from "@/dto/AdminBoardDto";
import { findById } from "@/apis/admin-board";
import { getAdminNmByToken } from "@/apis/admin";

interface UseBoardListProps {
  boardList: AdminBoardItemDto[];
}

export default function useBoardList({ boardList }: UseBoardListProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [filteredList, setFilteredList] = useState<AdminBoardItemDto[]>([]);
  const [selectedBoardItem, setSelectedBoardItem] =
    useState<AdminBoardItemDto | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentAdminId, setCurrentAdminId] = useState<string>("");

  // 카테고리 목록 추출 (중복 제거, 페이지와 섹션 모두 포함)
  const pageCategories = Array.from(
    new Set(boardList.map((item) => item.pageCategoryName))
  ).filter(Boolean);

  const sectionCategories = Array.from(
    new Set(boardList.map((item) => item.sectionCategoryName))
  ).filter(Boolean);

  // 현재 로그인한 관리자 정보 가져오기
  useEffect(() => {
    const fetchAdminInfo = async () => {
      try {
        // 관리자 정보를 가져온 후에, 게시판 항목의 admIdntfcId 형식에 맞게 변환해야 함
        const response = await getAdminNmByToken();
        if (response) {
          // 게시판 항목에 표시된 관리자 ID로 설정
          const firstItem = boardList.find(
            (item) => item.admName === response.admNm
          );
          if (firstItem) {
            setCurrentAdminId(firstItem.admIdntfcId);
          }
        }
      } catch (error) {
        console.error("관리자 정보 조회 중 오류 발생:", error);
      }
    };

    if (boardList.length > 0) {
      fetchAdminInfo();
    }
  }, [boardList]);

  // 목록 필터링
  useEffect(() => {
    const filtered = boardList.filter((item) => {
      const matchesSearch =
        searchTerm === "" ||
        item.boardTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.admName.toLowerCase().includes(searchTerm.toLowerCase());

      // 카테고리 필터 (페이지 또는 섹션 이름으로 필터링)
      const matchesCategory =
        !selectedCategory ||
        item.pageCategoryName === selectedCategory ||
        item.sectionCategoryName === selectedCategory;

      return matchesSearch && matchesCategory;
    });

    setFilteredList(filtered);
  }, [boardList, searchTerm, selectedCategory]);

  // 검색어 변경 핸들러
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // 카테고리 필터 변경 핸들러
  const handleCategoryClick = (category: string) => {
    if (selectedCategory === category) {
      setSelectedCategory(null); // 이미 선택된 카테고리 클릭 시 필터 해제
    } else {
      setSelectedCategory(category);
    }
  };

  // 게시물 클릭 핸들러
  const handleBoardItemClick = async (boardId: string) => {
    setIsDetailLoading(true);
    try {
      const response = await findById(boardId);
      if (response.success && response.data) {
        setSelectedBoardItem(response.data);
        setIsDetailModalOpen(true);
      }
    } catch (error) {
      console.error("게시물 상세 조회 중 오류 발생:", error);
    } finally {
      setIsDetailLoading(false);
    }
  };

  // 상세 모달 닫기 핸들러
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedBoardItem(null);
  };

  // 새 자료 요청 모달 열기 핸들러
  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  // 새 자료 요청 모달 닫기 핸들러
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  return {
    searchTerm,
    selectedCategory,
    filteredList,
    selectedBoardItem,
    isDetailModalOpen,
    isDetailLoading,
    isCreateModalOpen,
    pageCategories,
    sectionCategories,
    currentAdminId,
    handleSearchChange,
    handleCategoryClick,
    handleBoardItemClick,
    handleCloseDetailModal,
    handleOpenCreateModal,
    handleCloseCreateModal,
    setSelectedCategory,
  };
}
